import { useState } from 'react'

import environment from 'environment'

import { useAppSelector } from 'redux/hooks'
import { handleHighlight, handleMultiSelect } from 'redux/active/actions/multiSelectors'
import { CardType } from 'types/deck'

import PhatDropdown from 'components/formElements/PhatDropdown'
import BarChart, { ChartDatum } from '../BarChart'

import { SUBTYPE_ALLOWLIST } from 'components/deckPage/charts/charts.const'

import styles from './quantityTables.module.scss'

type GroupBy = 'types' | 'subTypes' | 'superType' | 'manaValue' | 'categories' | 'keywords'
type SizeKey = 'max' | 'large' | 'med' | 'small'

const BAR_WIDTH = 137

const chartOrientationClasses: Record<SizeKey, { horizontalClassName: string; verticalClassName: string }> = {
  max: { horizontalClassName: styles.horizontalChartMax, verticalClassName: styles.verticalChartMax },
  large: { horizontalClassName: styles.horizontalChartLarge, verticalClassName: styles.verticalChartLarge },
  med: { horizontalClassName: styles.horizontalChartMed, verticalClassName: styles.verticalChartMed },
  small: { horizontalClassName: styles.horizontalChartSmall, verticalClassName: styles.verticalChartSmall },
}

const QuantitiyTables = () => {
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const categories = useAppSelector(state => state.deck.categories)

  const [type, setType] = useState<GroupBy>('categories')
  const [typeForSubtypes, setTypeForSubtypes] = useState<string>('Creature')

  const handleSelectCards = (e: React.MouseEvent, cards: CardType[]) => {
    const controlKey = environment.getOnMac() ? e.metaKey : e.ctrlKey

    if (controlKey) return handleMultiSelect(cards)

    return handleHighlight(cards)
  }

  let largestValue = 0

  const deck = Object.values(cardMap)
  const probabilities: Record<string, { count: number; cards: CardType[] }> = {}

  for (const card of deck) {
    const primaryCategory = categories[card.categories[0]]

    // prettier-ignore
    if (!primaryCategory?.includedInDeck || primaryCategory?.name === 'Sideboard') continue

    const chartKeys = new Array<string | number>()

    const types = card.front && card.back ? [...card.front.types, ...card.back.types] : card.types
    const subtypes = card.front && card.back ? [...card.front.subTypes, ...card.back.subTypes] : card.subTypes
    const supertypes = card.front && card.back ? [...card.front.superTypes, ...card.back.superTypes] : card.superTypes

    if (type === 'types') chartKeys.push(...types)
    else if (type === 'manaValue') chartKeys.push(card.cmc)
    else if (type === 'categories') chartKeys.push(...card.categories)
    else if (type === 'keywords') chartKeys.push(...card.keywords)
    else if (type === 'superType') {
      const displaySupertypes = supertypes.length ? supertypes : [' No supertype']

      chartKeys.push(...displaySupertypes)
    } else if (type === 'subTypes') {
      if (!typeForSubtypes) chartKeys.push(...subtypes)
      if (types.includes(typeForSubtypes)) {
        const allowedSubtypesForCardType = SUBTYPE_ALLOWLIST[typeForSubtypes]
        const subTypeFilter = allowedSubtypesForCardType
          ? (subType: string) => allowedSubtypesForCardType.includes(subType)
          : () => true

        const subTypes = subtypes.filter(subTypeFilter)
        const displaySubTypes = subTypes.length ? subTypes : [' No subtype']

        chartKeys.push(...displaySubTypes)
      }
    }

    chartKeys.forEach(key => {
      if (!probabilities[key]) probabilities[key] = { count: 0, cards: [] }

      probabilities[key].count += card.qty
      probabilities[key].cards.push(card)

      if (probabilities[key].count > largestValue) largestValue = probabilities[key].count
    })
  }

  const data: ChartDatum[] = Object.keys(probabilities)
    .sort((a, b) => (a > b ? 1 : -1))
    .map(label => ({
      label,
      count: probabilities[label].count,
      onBarClick: (e: React.MouseEvent) => handleSelectCards(e, probabilities[label].cards),
    }))

  let sizeKey: SizeKey = 'max'

  if (data.length < 35) sizeKey = 'large'
  if (data.length < 25) sizeKey = 'med'
  if (data.length < 15) sizeKey = 'small'

  const { horizontalClassName, verticalClassName } = chartOrientationClasses[sizeKey]

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.controls}>
          <PhatDropdown
            inlineHeader
            header="Quantity of"
            triggerWrapperClassName={styles.typeSelector}
            value={type}
            options={[
              { label: 'Categories', onClick: () => setType('categories'), id: 'categories' },
              { label: 'Types', onClick: () => setType('types'), id: 'types' },
              { label: 'Sub Types', onClick: () => setType('subTypes'), id: 'subTypes' },
              { label: 'Super Types', onClick: () => setType('superType'), id: 'superType' },
              { label: 'Mana Value', onClick: () => setType('manaValue'), id: 'manaValue' },
              { label: 'Keywords', onClick: () => setType('keywords'), id: 'keywords' },
            ]}
          />

          {type === 'subTypes' && (
            <PhatDropdown
              inlineHeader
              header="Card type"
              triggerWrapperClassName={styles.typeSelector}
              value={typeForSubtypes}
              options={[
                { label: 'All card types', onClick: () => setTypeForSubtypes(''), id: '' },
                { type: 'spacer' },
                { label: 'Creatures', onClick: () => setTypeForSubtypes('Creature'), id: 'Creature' },
                { label: 'Artifacts', onClick: () => setTypeForSubtypes('Artifact'), id: 'Artifact' },
                { label: 'Enchantments', onClick: () => setTypeForSubtypes('Enchantment'), id: 'Enchantment' },
                { label: 'Instants', onClick: () => setTypeForSubtypes('Instant'), id: 'Instant' },
                { label: 'Sorceries', onClick: () => setTypeForSubtypes('Sorcery'), id: 'Sorcery' },
                { label: 'Planeswalkers', onClick: () => setTypeForSubtypes('Planeswalker'), id: 'Planeswalker' },
                { label: 'Lands', onClick: () => setTypeForSubtypes('Land'), id: 'Land' },
              ]}
            />
          )}
        </div>

        <BarChart data={data} containerClassName={horizontalClassName} labelContainerClassName={styles.chartLabels} />

        <div className={verticalClassName}>
          {Object.keys(probabilities)
            .sort((a, b) => (a > b ? 1 : -1))
            .map(label => {
              const { count, cards } = probabilities[label]

              return (
                <button onClick={e => handleSelectCards(e, cards)} key={label}>
                  <label className={styles.label}>{label}</label>
                  <label className={styles.quantity}>{count}</label>
                  <span className={styles.bar} style={{ width: `${(count / largestValue || 0) * BAR_WIDTH}px` }} />
                </button>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default QuantitiyTables
